import { fmtPrice } from "@/data/mellow";
import { Link } from "react-router-dom";
import FlowerButton from "@/components/FlowerButton";
import { StockBadge, useStockInfo } from "@/components/StockBadge";
import { useCart } from "@/context/CartContext";
import { useState } from "react";

const Gallery = ({ product }) => {
  const images = product.gallery || [{ src: product.img, alt: product.name }];
  const [active, setActive] = useState(0);
  return (
    <div className="relative mx-auto w-full max-w-[420px]">
      <div className="absolute -inset-4 blob-sage opacity-70 blur-2xl pointer-events-none" aria-hidden="true"></div>
      <img
        key={images[active].src}
        src={images[active].src}
        alt={images[active].alt}
        className="relative w-full rounded-[1.75rem] shadow-[0_20px_60px_rgb(0,0,0,0.10)] animate-float-slow"
        data-testid="spotlight-cover"
      />
      {images.length > 1 && (
        <div className="relative mt-4 flex flex-wrap justify-center gap-3" data-testid="spotlight-gallery">
          {images.map((im, i) => (
            <button
              key={im.src}
              type="button"
              onClick={() => setActive(i)}
              aria-label={im.alt}
              aria-pressed={i === active}
              className={`w-14 h-14 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 transition-all duration-200 ${i === active ? "border-[#78353A] scale-105" : "border-dashed border-[#D1D5CB] opacity-70 hover:opacity-100"}`}
              data-testid={`spotlight-thumb-${i}`}
            >
              <img src={im.src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProductSpotlight = ({ product, eyebrow = "Notre carnet" }) => {
  const { add } = useCart();
  const { soldOut } = useStockInfo(product.lookup_key);
  return (
    <section
      className="relative rounded-[2.5rem] border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-6 md:p-10 overflow-hidden"
      data-testid={`product-spotlight-${product.lookup_key}`}
    >
      <span className="daisy-deco absolute top-6 right-8 animate-wiggle" aria-hidden="true"></span>
      <div className="grid lg:grid-cols-[minmax(0,420px)_1fr] gap-10 items-start min-w-0">
        <Gallery product={product} />
        <div className="min-w-0">
          <p className="font-cursive text-2xl text-[#78353A]/70">{eyebrow}</p>
          <h2 className="font-cursive text-5xl md:text-6xl text-[#78353A] leading-none">{product.name}</h2>
          <p className="mt-4 text-[#4A3B32]/90 leading-relaxed max-w-xl">{product.desc}</p>

          {product.details && (
            <ul className="mt-5 flex flex-wrap gap-2" data-testid="spotlight-details">
              {product.details.map((d) => (
                <li key={d} className="text-sm px-3 py-1 rounded-full bg-[#EAECE1] text-[#4A3B32]">{d}</li>
              ))}
            </ul>
          )}
          {product.themes && (
            <div className="mt-7">
              <h3 className="font-cursive text-3xl text-[#78353A] mb-3">Les 8 thèmes du carnet</h3>
              <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2" data-testid="spotlight-themes">
                {product.themes.map((t, i) => (
                  <li key={t.title} className="flex items-baseline gap-3 text-[#4A3B32]/90">
                    <span className="font-cursive text-2xl text-[#78353A] w-6 shrink-0">{i + 1}.</span>
                    <span>{t.title}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-6">
            <span className="font-cursive text-5xl text-[#4A3B32] leading-none" data-testid="spotlight-price">
              {fmtPrice(product.price)}
              {product.compareAt && <span className="ml-3 align-middle text-2xl text-[#4A3B32]/50 line-through">{fmtPrice(product.compareAt)}</span>}
            </span>
            <FlowerButton
              variant="pink"
              onClick={() => add(product.lookup_key)}
              disabled={soldOut}
              data-testid={`add-to-cart-${product.lookup_key}`}
            >
              {soldOut ? "Épuisé" : "Ajouter au panier"}
            </FlowerButton>
            <StockBadge lookup_key={product.lookup_key} />
            {product.lookup_key === "green-days-carnet" ? (
              <Link to="/feuilleter" className="font-cursive text-2xl text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="spotlight-feuilleter-link">
                Feuilleter le carnet
              </Link>
            ) : (
              <Link to={`/produit/${product.lookup_key}`} className="font-cursive text-2xl text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="spotlight-product-link">
                Voir la fiche produit
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSpotlight;
